<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Header -->
      <div class="header">
        <i class="fa-solid fa-sliders-up"></i>
        <h2>{{ $t("components.chargerlistitemfilter.filtros") }}</h2>
        <button class="close-button" @click="closeModal">✕</button>
      </div>

      <!-- Filter Content -->
      <div class="filter-content">
        <!-- 1. Estado del cargador -->
        <div class="filter-section">
          <label class="filter-label">{{ $t("components.chargerlistitemfilter.estado_cargador") }}</label>
          <div class="pill-group">
            <button class="pill" :class="{ active: chargerStatus === 'libre' }" @click="chargerStatus = 'libre'">
              {{ $t("components.chargerlistitemfilter.libre") }}
            </button>
            <button class="pill" :class="{ active: chargerStatus === 'todos' }" @click="chargerStatus = 'todos'">
              {{ $t("components.chargerlistitemfilter.todos") }}
            </button>
          </div>
        </div>

        <!-- 2. Precio máximo (EUR/kWh) -->
        <div class="filter-section">
          <label class="filter-label">{{ $t("components.chargerlistitemfilter.precio_maximo") }}</label>
          <div class="input-group">
            <input type="number" v-model="priceMax" step="0.01" min="0" maxlength="5" class="form-control text-end"
              placeholder="0.60" @input="handlePriceInput" />
            <span class="input-group-text">€/kWh</span>
          </div>
        </div>

        <!-- 3. Distancia -->
        <div v-if="!hideDistance" class="filter-section">
          <label class="filter-label">{{ $t("components.chargerlistitemfilter.distancia") }}</label>
          <div class="distance-subtitle">{{ $t("components.chargerlistitemfilter.en_radio") }}</div>
          <div class="distance-input-group">
            <input type="range" v-model="distance" min="1" max="50" step="1" class="form-range" />
            <div class="slider-value">{{ distance }} km</div>
          </div>
        </div>

        <!-- 4. Tiempo de carga estimado -->
        <div class="filter-section">
          <label class="filter-label">{{ $t("components.chargerlistitemfilter.tiempo_carga_estimado") }}</label>
          <div class="time-options">
            <div class="time-row">
              <button class="pill" :class="{ active: chargingTime === '<15' }" @click="chargingTime = '<15'">
                {{ $t("components.chargerlistitemfilter.menos_15") }}
              </button>
              <button class="pill" :class="{ active: chargingTime === '<30' }" @click="chargingTime = '<30'">
                {{ $t("components.chargerlistitemfilter.menos_30") }}
              </button>
              <button class="pill" :class="{ active: chargingTime === '<45' }" @click="chargingTime = '<45'">
                {{ $t("components.chargerlistitemfilter.menos_45") }}
              </button>
            </div>
            <div class="time-row">
              <button class="pill" :class="{ active: chargingTime === '<60' }" @click="chargingTime = '<60'">
                {{ $t("components.chargerlistitemfilter.menos_60") }}
              </button>
              <button class="pill" :class="{ active: chargingTime === 'any' }" @click="chargingTime = 'any'">
                {{ $t("components.chargerlistitemfilter.cualquiera") }}
              </button>
            </div>
          </div>
        </div>
        <!--div class="results-info">Mostrando {{ resultsCount }} resultados</div-->
      </div>

      <!-- Footer -->
      <div class="actions">
        <button class="btn btn-secondary" @click="clearFilters">{{ $t("components.chargerlistitemfilter.limpiar") }}</button>

        <button class="btn btn-primary" @click="applyFilters">{{ $t("components.chargerlistitemfilter.aplicar_filtros") }}</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from "vue";

// Props y emits
interface Props {
  activeFilters?: {
    chargerStatus: string;
    priceMax: number;
    distance: number;
    chargingTime: string;
  } | null;
  hideDistance?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  activeFilters: null,
  hideDistance: false
});

const emit = defineEmits<{
  close: [];
  apply: [filters: {
    chargerStatus: string;
    priceMax: number;
    distance: number;
    chargingTime: string;
  }];
}>();

// Estado de los filtros (valores por defecto)
const chargerStatus = ref<string>("todos");
const priceMax = ref<number>(1);
const distance = ref<number>(10);
const chargingTime = ref<string>("any");
const resultsCount = ref<number>(12);

// Inicializar filtros con los valores activos si existen
function initializeFilters() {
  if (props.activeFilters) {
    chargerStatus.value = props.activeFilters.chargerStatus || "todos";
    priceMax.value = props.activeFilters.priceMax || 1;
    distance.value = props.activeFilters.distance || 10;
    chargingTime.value = props.activeFilters.chargingTime || "any";
  } else {
    // Si no hay filtros activos, usar valores por defecto
    chargerStatus.value = "todos";
    priceMax.value = 1;
    distance.value = 10;
    chargingTime.value = "any";
  }
}

// Inicializar al montar el componente
onMounted(() => {
  initializeFilters();
});

// Actualizar cuando cambien los props
watch(() => props.activeFilters, () => {
  initializeFilters();
}, { deep: true });

function handlePriceInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // Limitar a 5 caracteres
  if (value.length > 5) {
    input.value = value.slice(0, 5);
    priceMax.value = parseFloat(value.slice(0, 5)) || 0;
  }
}

function closeModal() {
  emit("close");
}

function clearFilters() {
  chargerStatus.value = "todos";
  priceMax.value = 1;
  distance.value = 10;
  chargingTime.value = "any";
}

function applyFilters() {
  // Emitir los filtros al componente padre
  emit("apply", {
    chargerStatus: chargerStatus.value,
    priceMax: priceMax.value,
    distance: distance.value,
    chargingTime: chargingTime.value
  });
  closeModal();
}
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/figma.scss";
@import "@/styles/_themevars.scss";


.filter-content {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: 0 var(--spacing-md) var(--spacing-md);
  overflow: auto;
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  color: var(--text-default-default, #000000);
}

.placeholder-text {
  color: #9ca3af;
  font-style: italic;
  font-size: 14px;
}

.pill-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pill {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: var(--background-default-default, #FFF);
  color: var(--text-default-default, #000000);
  border-radius: var(--border-radius-rounded, 100%);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.pill:hover {
  background: #f3f4f6;
}

.pill.active {
  background: var(--background-secondary-default, #1863ED);
  color: var(--text-default-inverse, #FFF);
  border-color: var(--border-secondary-default, #1863ED);
}

.distance-subtitle {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 14px);
}

.distance-input-group {
  display: flex;
  align-items: center;
  gap: 8px;

  .slider-value {
    color: var(--text-default-caption, #667790);
    font-size: var(--font-size-heading-h3, 20px);
    font-weight: 700;
    min-width: 64px;
  }
}

.time-options {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);

  .time-row {
    display: flex;
    gap: var(--spacing-xs);
    flex-wrap: wrap;
  }
}



.results-info {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 14px);
  font-weight: 500;
}
</style>
